'use client';

import type { CSSProperties, HTMLAttributes } from 'react';
import { cx } from '../utils/cx';
import { resolveSpaceCssLength } from '../utils/spacing';

export type GridCols = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

const GRID_COLS_CLASS: Record<GridCols, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  7: 'grid-cols-7',
  8: 'grid-cols-8',
  9: 'grid-cols-9',
  10: 'grid-cols-10',
  11: 'grid-cols-11',
  12: 'grid-cols-12',
};

const GRID_COLS_SM_CLASS: Record<GridCols, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
  5: 'sm:grid-cols-5',
  6: 'sm:grid-cols-6',
  7: 'sm:grid-cols-7',
  8: 'sm:grid-cols-8',
  9: 'sm:grid-cols-9',
  10: 'sm:grid-cols-10',
  11: 'sm:grid-cols-11',
  12: 'sm:grid-cols-12',
};

const GRID_COLS_MD_CLASS: Record<GridCols, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
  6: 'md:grid-cols-6',
  7: 'md:grid-cols-7',
  8: 'md:grid-cols-8',
  9: 'md:grid-cols-9',
  10: 'md:grid-cols-10',
  11: 'md:grid-cols-11',
  12: 'md:grid-cols-12',
};

const GRID_COLS_LG_CLASS: Record<GridCols, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
  7: 'lg:grid-cols-7',
  8: 'lg:grid-cols-8',
  9: 'lg:grid-cols-9',
  10: 'lg:grid-cols-10',
  11: 'lg:grid-cols-11',
  12: 'lg:grid-cols-12',
};

const GRID_COLS_XL_CLASS: Record<GridCols, string> = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  5: 'xl:grid-cols-5',
  6: 'xl:grid-cols-6',
  7: 'xl:grid-cols-7',
  8: 'xl:grid-cols-8',
  9: 'xl:grid-cols-9',
  10: 'xl:grid-cols-10',
  11: 'xl:grid-cols-11',
  12: 'xl:grid-cols-12',
};

const GRID_COLS_2XL_CLASS: Record<GridCols, string> = {
  1: '2xl:grid-cols-1',
  2: '2xl:grid-cols-2',
  3: '2xl:grid-cols-3',
  4: '2xl:grid-cols-4',
  5: '2xl:grid-cols-5',
  6: '2xl:grid-cols-6',
  7: '2xl:grid-cols-7',
  8: '2xl:grid-cols-8',
  9: '2xl:grid-cols-9',
  10: '2xl:grid-cols-10',
  11: '2xl:grid-cols-11',
  12: '2xl:grid-cols-12',
};

export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Gap between grid cells:
   * - token: "xs" | "sm" | "md" | "lg" | "xl" | "2xl"
   * - number: spacing units (multiplied by `--spacing`)
   * - css length: "12px", "1rem", "clamp(...)"
   */
  gap?: number | string;

  cols?: GridCols;
  colsSm?: GridCols;
  colsMd?: GridCols;
  colsLg?: GridCols;
  colsXl?: GridCols;
  cols2xl?: GridCols;

  className?: string;
  children: React.ReactNode;
}

const resolveGapStyle = (gap: number | string | undefined): CSSProperties => {
  const css = resolveSpaceCssLength(gap, 2);
  if (!css) return {};
  return { gap: css };
};

export function Grid({ className, children, gap, cols, colsSm, colsMd, colsLg, colsXl, cols2xl, style, ...rest }: GridProps) {
  const defaultCols = !cols && !className ? GRID_COLS_CLASS[1] : null;

  return (
    <div
      {...rest}
      style={{ ...resolveGapStyle(gap), ...style }}
      className={cx(
        'grid',
        defaultCols,
        cols ? GRID_COLS_CLASS[cols] : null,
        colsSm ? GRID_COLS_SM_CLASS[colsSm] : null,
        colsMd ? GRID_COLS_MD_CLASS[colsMd] : null,
        colsLg ? GRID_COLS_LG_CLASS[colsLg] : null,
        colsXl ? GRID_COLS_XL_CLASS[colsXl] : null,
        cols2xl ? GRID_COLS_2XL_CLASS[cols2xl] : null,
        className,
      )}
    >
      {children}
    </div>
  );
}

Grid.displayName = 'Grid';
